import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import BarChartIcon from '@material-ui/icons/BarChart';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import { useAuth } from '../../../context/auth';
import { DQ_PRO_PLAN_KEY } from '../../../utils/constants';
import { listClientsService } from '../../../services/client.service';
import {
    usePartnerDashboardSections, schoolRowsFrom, programKpi, usePartnerWorkspace, outcomesFrom,
} from './partnerPortfolioData';

/**
 * Growth Opportunities > Portfolio Snapshot > Executive Overview — Partner
 * workspace. Built from three reference mockups (tagged P0, P1, LC). The figures are
 * real (LA-PW-20Oct-02): schools, participation and statuses from the
 * Partner Dashboard's S_8 By School, program figures from its S_20 Program
 * Deployment KPIs, licences from the My Schools list; Priority Areas, Active
 * Teachers and Learning outcomes from the partner workspace (PW) document's
 * portfolio.block3 / block4 (outcomes "—" when suppressed, n < 10). This is a new,
 * standalone page under Growth Opportunities, not a reuse of the existing
 * Partner Dashboard Summary page — the mockup's "previously in the P0 Partner
 * Dashboard, moved to main Workspace" note is a design history note, not an
 * instruction to share that page's component or data source.
 *
 * Content: one base group (Reach + Schools Need Attention) every plan sees,
 * plus a plan-specific Adoption & Outcomes group:
 *   - P0: Reach + Need Attention only (2 cards).
 *   - P1 (enterprise_p1, enterprise_p1a, or enterprise_p1b — the base plan
 *     plus both product-access variants): + "My Program Adoption" and
 *     Learning outcomes, combined into one third card.
 *   - LC (enterprise_lc): + "Teacher-Created Programs" and Learning
 *     outcomes, combined into one third card.
 * "Intention for change" (61%) was struck through in both the P1 and LC
 * mockups' Learning outcomes row, so it's omitted — only the first two rows
 * are built.
 *
 * Layout: per feedback that the earlier stacked-sections version didn't
 * read well, this uses the 3-card side-by-side pattern already established
 * on the Solutions pages (icon-badge CardHeader + compact label/value rows
 * inside a bordered card) instead of repeating grids of individual stat
 * tiles — fewer, denser cards instead of a long vertical stack.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};

const eyebrowSx = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: MUTED,
};

const cardHeaderIconSx = {
    width: 36, height: 36, borderRadius: '10px',
    backgroundColor: 'rgba(255, 105, 0, 0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};

const CardHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, mb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={cardHeaderIconSx}>
            <Icon sx={{ fontSize: 20, color: ORANGE }} />
        </Box>
        <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
            {title}
        </Typography>
    </Box>
);

const StatRow = ({ label, value, sub, valueColor, pillColor, last }) => (
    <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2,
        py: 1.5, borderBottom: last ? 'none' : `1px solid ${BORDER}`,
    }}>
        <Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: TEXT }}>{label}</Typography>
            {sub && <Typography sx={{ fontSize: '12px', color: MUTED, mt: 0.25 }}>{sub}</Typography>}
        </Box>
        {pillColor ? (
            <Box sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: pillColor, color: '#ffffff', fontSize: '13px', fontWeight: 800,
                minWidth: '50px', px: 1, py: 0.4, borderRadius: '50px', flexShrink: 0,
            }}>
                {value}
            </Box>
        ) : (
            <Typography sx={{ fontSize: '20px', fontWeight: 800, color: valueColor || TEXT, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {value}
            </Typography>
        )}
    </Box>
);

const OutcomeTile = ({ label, value }) => (
    <Box sx={{ ...plainCardSx, p: 3 }}>
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255, 105, 0, 0.08)', borderRadius: '10px',
            px: 2, py: 1, mb: 1.5,
        }}>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: ORANGE, lineHeight: 1 }}>
                {value}
            </Typography>
        </Box>
        <Typography sx={{ fontSize: '14px', color: TEXT }}>{label}</Typography>
    </Box>
);

// Same solid fills as DEPLOYMENT_ACTION_BAND_COLORS (utils/constants.js),
// used by every other dashboard's deployment-status chip (e.g.
// PartnerDashboard/pages/AttentionNeeded) — not a fresh palette.
// status: the school status (getSchoolStatus) each row counts.
const NEED_ATTENTION_ROWS = [
    { id: 'dormant', label: 'Dormant', status: 'Dormant', color: '#9CA3AF' },
    { id: 'data_weak', label: 'Data Weak', status: 'Data weak', color: '#EFA020' },
    { id: 'low_coverage', label: 'Low Coverage', status: 'Low coverage', color: '#E8730C' },
    // Not shown for LC — its mockup's Schools Need Attention panel has no
    // High Risk row.
    { id: 'high_risk', label: 'High Risk', status: 'High risk', color: '#E04535' },
];

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

const PortfolioExecutiveOverviewPage = () => {
    const { authUser } = useAuth();
    const planKey = authUser?.dq_pro_plan?.plan_key;
    const isP1 = [DQ_PRO_PLAN_KEY.ENTERPRISE_P1, DQ_PRO_PLAN_KEY.ENTERPRISE_P1A, DQ_PRO_PLAN_KEY.ENTERPRISE_P1B].includes(planKey);
    const isLC = planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_LC;

    const { data } = usePartnerDashboardSections(['S_8', 'S_20']);
    const schools = data ? schoolRowsFrom(data) : null;
    const sum = (key) => (schools ? schools.reduce((n, r) => n + r[key], 0) : null);
    const activeSchools = sum('activeSchools');
    const activeParticipants = sum('activeParticipants');
    const registeredParticipants = sum('registeredParticipants');
    const participationPct = registeredParticipants
        ? `${Math.round((activeParticipants / registeredParticipants) * 100)}%`
        : '—';
    const countStatus = (status) => (schools ? schools.filter((r) => r.status === status).length : null);

    const { data: workspace } = usePartnerWorkspace();
    const block3 = workspace?.portfolio?.block3;
    const learningOutcomes = outcomesFrom(workspace);

    // Licences given to the partner's schools and networks — the My Schools rows.
    // The list API returns at most 100 rows a call, so every page is read.
    const [licenses, setLicenses] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const PAGE_SIZE = 100;
        (async () => {
            const clients = [];
            for (let page = 1; ; page += 1) {
                const res = await listClientsService({ page, limit: PAGE_SIZE });
                const rows = res?.data?.clients || [];
                clients.push(...rows);
                if (rows.length < PAGE_SIZE || clients.length >= (res?.data?.total || 0)) break;
            }
            if (!cancelled) setLicenses({
                total: clients.reduce((n, c) => n + (c.licenses_total || 0), 0),
                used: clients.reduce((n, c) => n + (c.licenses_used || 0), 0),
            });
        })().catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const needAttentionRows = (isLC
        ? NEED_ATTENTION_ROWS.filter((row) => row.id !== 'high_risk')
        : NEED_ATTENTION_ROWS
    ).map((row) => ({ ...row, value: fmt(countStatus(row.status)) }));
    const needAttentionTotal = schools
        ? needAttentionRows.reduce((n, row) => n + countStatus(row.status), 0)
        : null;

    const myPrograms = programKpi(data, 'C_2');
    const teacherPrograms = programKpi(data, 'C_6');

    const participationCaption = isP1
        ? `${fmt(activeParticipants)} (assessed & learned) / ${fmt(registeredParticipants)} (registered)`
        : isLC
            ? `${fmt(activeParticipants)} (learned) / ${fmt(registeredParticipants)} (registered)`
            : `${fmt(activeParticipants)} (assessed) / ${fmt(registeredParticipants)} (registered)`;

    const programSection = isP1
        ? {
            title: 'My Program Adoption',
            rows: [
                { label: 'Programs Published', value: fmt(myPrograms?.deployed_count), sub: `of ${fmt(myPrograms?.created_count)} programs created` },
                { label: 'Schools Assigned', value: fmt(myPrograms?.units_assigned), sub: 'schools receiving at least 1 program' },
                { label: 'Students Reached', value: fmt(myPrograms?.enrolled), sub: 'students enrolled' },
                {
                    label: 'Priority Areas Covered',
                    value: fmt(block3?.areas_covered),
                    sub: block3?.areas_total != null ? `of ${block3.areas_total} priority areas` : 'priority areas',
                },
            ],
        }
        : isLC
            ? {
                title: 'Teacher-Created Programs',
                rows: [
                    { label: 'Programs Published', value: fmt(teacherPrograms?.deployed_count), sub: `of ${fmt(teacherPrograms?.created_count)} programs created` },
                    { label: 'Schools Activated', value: fmt(teacherPrograms?.units_assigned), sub: 'schools assigned a teacher-created program' },
                    { label: 'Students Reached', value: fmt(teacherPrograms?.enrolled), sub: 'students enrolled' },
                    { label: 'Active Teachers', value: fmt(block3?.active_teachers), sub: 'teachers using LearnCreator' },
                ],
            }
            : null;

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
                Portfolio Executive Overview
            </Typography>
            <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 4 }}>
                Which countries, clients and products need partner attention?
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, alignItems: 'stretch' }}>
                {/* Card 1 — Reach */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={BarChartIcon} title={isP1 ? 'Assessment Reach' : 'Baseline Assessment Reach'} />
                    <Box>
                        <StatRow label="Active Schools" sub="Schools" value={fmt(activeSchools)} />
                        <StatRow label="Licenses Assigned" sub={licenses ? `${fmt(licenses.used)} in use` : ''} value={fmt(licenses?.total)} />
                        <StatRow label="Student Participation" sub={participationCaption} value={participationPct} />
                        <StatRow label="Need Attention" sub="Schools" value={fmt(needAttentionTotal)} last />
                    </Box>
                </Box>

                {/* Card 2 — Schools Need Attention */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={ReportProblemOutlinedIcon} title="Schools Need Attention" />
                    <Box>
                        {needAttentionRows.map((row, i) => (
                            <StatRow
                                key={row.label}
                                label={row.label}
                                value={row.value}
                                pillColor={row.color}
                                last={i === needAttentionRows.length - 1}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Card 3 — plan-specific program adoption */}
                {programSection && (
                    <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader icon={MenuBookOutlinedIcon} title={programSection.title} />
                        <Box>
                            {programSection.rows.map((row, i) => (
                                <StatRow
                                    key={row.label}
                                    label={row.label}
                                    value={row.value}
                                    sub={row.sub}
                                    last={i === programSection.rows.length - 1}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Learning outcomes — its own row below the 3 cards */}
            {programSection && (
                <Box sx={{ mt: 3 }}>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT, mb: 2 }}>
                        Learning outcomes
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2.5 }}>
                        {learningOutcomes.map((o) => (
                            <OutcomeTile key={o.label} label={o.label} value={o.value} />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PortfolioExecutiveOverviewPage;
